import React from 'react';
import {
  Camera,
  ChevronDown,
  ChevronUp,
  FilePlus,
  FileText,
  Image,
  Minus,
  Search,
  Trash2,
  Upload,
} from 'lucide-react';
import { InlineLoadingState } from '../../../components/LoadingSpinner';
import { formatDateToDDMMYYYY } from '../../../utils/date';
import {
  formatLeadDocumentSize,
  getFileTypeStyle,
  getLeadDocumentUrl,
  isLeadDocumentDeletable,
  MAX_LEAD_DOCUMENT_UPLOAD_BYTES,
  partitionLeadDocuments,
  type LeadDocument,
} from '../utils/leadDocumentFiles';
import {
  DocumentUploadProgressBanner,
  DocumentUploadProgressOverlay,
  type DocumentUploadProgress,
} from './DocumentUploadProgress';

type DocumentSort = { key: 'filename' | 'size' | 'created_at'; dir: 'asc' | 'desc' };

export type LeadDocumentsTabProps = {
  leadId: string | number;
  documents: LeadDocument[];
  documentsLoading: boolean;
  documentsUploading: boolean;
  documentsUploadProgress: DocumentUploadProgress | null;
  documentsUploadError?: string | null;
  isDragging: boolean;
  documentSearch: string;
  onDocumentSearchChange: (value: string) => void;
  documentSort: DocumentSort;
  onToggleSort: (key: DocumentSort['key']) => void;
  selectedDocIds: Set<number>;
  onToggleSelectAll: () => void;
  onToggleSelectDoc: (id: number) => void;
  filteredAndSortedDocuments: LeadDocument[];
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  cameraInputRef: React.RefObject<HTMLInputElement | null>;
  onUploadDocument: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDrop: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onPreview: (doc: LeadDocument) => void;
  onDelete: (docId: number) => void;
  onCreateDocument: () => void;
  /** In eine bestehende Karte eingebettet: eigene Überschrift und Scroll-/Padding-Rahmen entfallen. */
  embedded?: boolean;
};

function CountBadge({ label, count }: { label: string; count: number }) {
  if (count === 0) return null;
  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-blue-100 text-blue-800">
      {count} {label}
    </span>
  );
}

function FileTypeBadge({
  filename,
  className = 'w-14 h-14',
}: {
  filename: string;
  className?: string;
}) {
  const typeStyle = getFileTypeStyle(filename);
  return (
    <div
      className={`rounded-lg flex items-center justify-center text-[10px] font-bold shrink-0 overflow-hidden ${className} ${typeStyle.bg} ${typeStyle.text}`}
    >
      <span>{typeStyle.label}</span>
    </div>
  );
}

function DocumentActions({
  onOpen,
  onDelete,
  compact,
}: {
  onOpen: () => void;
  onDelete?: () => void;
  compact?: boolean;
}) {
  if (compact) {
    return (
      <div className="flex items-center gap-1.5 shrink-0">
        <button
          type="button"
          onClick={onOpen}
          className="p-1.5 rounded-lg hover:bg-app-hover text-pipedrive-blue"
          aria-label="Öffnen"
        >
          <Search className="w-4 h-4" />
        </button>
        {onDelete ? (
          <button
            type="button"
            onClick={onDelete}
            className="p-1.5 rounded-lg hover:bg-red-50 text-red-500"
            aria-label="Löschen"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        ) : null}
      </div>
    );
  }
  return (
    <div className="flex items-center gap-3">
      <button type="button" onClick={onOpen} className="text-pipedrive-blue hover:underline font-medium">
        Öffnen
      </button>
      {onDelete ? (
        <button type="button" onClick={onDelete} className="text-red-600 hover:underline font-medium">
          Löschen
        </button>
      ) : null}
    </div>
  );
}

function PdfDocumentsSection({
  pdfs,
  documentSort,
  onToggleSort,
  selectedDocIds,
  onToggleSelectAll,
  onToggleSelectDoc,
  onPreview,
  onDelete,
}: {
  pdfs: LeadDocument[];
  documentSort: DocumentSort;
  onToggleSort: (key: DocumentSort['key']) => void;
  selectedDocIds: Set<number>;
  onToggleSelectAll: () => void;
  onToggleSelectDoc: (id: number) => void;
  onPreview: (doc: LeadDocument) => void;
  onDelete: (docId: number) => void;
}) {
  return (
    <section className="mb-8">
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <FileText className="w-4 h-4 text-app-text-subtle" />
        <h3 className="text-lg font-bold text-app-text">Dokumente</h3>
        <CountBadge label="PDFs" count={pdfs.length} />
      </div>
      {pdfs.length === 0 ? (
        <p className="text-sm text-app-text-subtle py-4 text-center sm:text-left">Noch keine PDF-Dokumente vorhanden.</p>
      ) : (
        <>
          <div className="sm:hidden divide-y divide-app-border border border-app-border rounded-lg overflow-hidden">
            {pdfs.map((doc) => (
                <div key={doc.id} className="flex items-center gap-3 px-4 py-3 bg-app-surface">
                  <FileTypeBadge filename={doc.filename} className="w-12 h-12" />
                  <div className="min-w-0 flex-1">
                    <button
                      type="button"
                      onClick={() => onPreview(doc)}
                      className="text-sm font-medium text-app-text hover:text-pipedrive-blue truncate block text-left max-w-full"
                    >
                      {doc.filename}
                    </button>
                    <p className="text-xs text-slate-400">
                      {formatLeadDocumentSize(doc.size)} · {formatDateToDDMMYYYY(doc.created_at.slice(0, 10))}
                    </p>
                  </div>
                  <DocumentActions
                    compact
                    onOpen={() => onPreview(doc)}
                    onDelete={isLeadDocumentDeletable(doc) ? () => onDelete(doc.id) : undefined}
                  />
                </div>
              ))}
          </div>
          <div className="hidden sm:block border border-app-border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-app-surface-muted border-b border-app-border">
                  <th className="w-10 px-4 py-3 text-left">
                    <button
                      type="button"
                      onClick={onToggleSelectAll}
                      className="p-1 rounded-full hover:bg-blue-100 text-pipedrive-blue"
                      title={selectedDocIds.size === pdfs.length && pdfs.length > 0 ? 'Alle abwählen' : 'Alle auswählen'}
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <button
                      type="button"
                      onClick={() => onToggleSort('filename')}
                      className="inline-flex items-center gap-1 font-semibold text-app-text-muted hover:text-pipedrive-blue"
                    >
                      Dateiname
                      {documentSort.key === 'filename' ? (
                        documentSort.dir === 'asc' ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )
                      ) : (
                        <ChevronUp className="w-4 h-4 opacity-30" />
                      )}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <button
                      type="button"
                      onClick={() => onToggleSort('size')}
                      className="inline-flex items-center gap-1 font-semibold text-app-text-muted hover:text-pipedrive-blue"
                    >
                      Größe
                      {documentSort.key === 'size' ? (
                        documentSort.dir === 'asc' ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )
                      ) : (
                        <ChevronUp className="w-4 h-4 opacity-30" />
                      )}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <button
                      type="button"
                      onClick={() => onToggleSort('created_at')}
                      className="inline-flex items-center gap-1 font-semibold text-app-text-muted hover:text-pipedrive-blue"
                    >
                      Zuletzt geändert
                      {documentSort.key === 'created_at' ? (
                        documentSort.dir === 'asc' ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )
                      ) : (
                        <ChevronUp className="w-4 h-4 opacity-30" />
                      )}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left font-semibold text-app-text-muted w-28">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {pdfs.map((doc) => (
                    <tr key={doc.id} className="border-b border-app-border hover:bg-app-hover/50">
                      <td className="px-4 py-2">
                        <input
                          type="checkbox"
                          checked={selectedDocIds.has(doc.id)}
                          onChange={() => onToggleSelectDoc(doc.id)}
                          className="rounded border-app-border-strong text-pipedrive-blue focus:ring-pipedrive-blue"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-2.5">
                          <FileTypeBadge filename={doc.filename} className="w-9 h-9" />
                          <button
                            type="button"
                            onClick={() => onPreview(doc)}
                            className="font-medium text-app-text hover:text-pipedrive-blue hover:underline truncate text-left max-w-full"
                          >
                            {doc.filename}
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-2 text-app-text-muted whitespace-nowrap">{formatLeadDocumentSize(doc.size)}</td>
                      <td className="px-4 py-2 text-app-text-muted whitespace-nowrap">
                        {formatDateToDDMMYYYY(doc.created_at.slice(0, 10))}
                      </td>
                      <td className="px-4 py-2">
                        <DocumentActions
                          onOpen={() => onPreview(doc)}
                          onDelete={isLeadDocumentDeletable(doc) ? () => onDelete(doc.id) : undefined}
                        />
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

function LeadDocumentThumbnail({
  src,
  filename,
}: {
  src: string;
  filename: string;
}) {
  const [failed, setFailed] = React.useState(false);
  const typeStyle = getFileTypeStyle(filename);
  if (failed) {
    return (
      <div className={`flex h-full w-full items-center justify-center ${typeStyle.bg}`}>
        <span className={`text-xs font-semibold ${typeStyle.text}`}>{typeStyle.label}</span>
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={filename}
      className="h-full w-full object-cover"
      onError={() => setFailed(true)}
    />
  );
}

function ImagesSection({
  leadId,
  images,
  onPreview,
  onDelete,
}: {
  leadId: string | number;
  images: LeadDocument[];
  onPreview: (doc: LeadDocument) => void;
  onDelete: (docId: number) => void;
}) {
  return (
    <section className="mb-8">
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <Image className="w-4 h-4 text-app-text-subtle" />
        <h3 className="text-lg font-bold text-app-text">Bilder</h3>
        <CountBadge label="Bilder" count={images.length} />
      </div>
      {images.length === 0 ? (
        <p className="text-sm text-app-text-subtle py-4 text-center sm:text-left">Noch keine Bilder vorhanden.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
          {images.map((doc) => {
            const docUrl = getLeadDocumentUrl(leadId, doc);
            return (
              <div
                key={doc.id}
                className="group relative rounded-lg border border-app-border bg-app-surface overflow-hidden hover:border-app-border-strong transition-colors"
              >
                <button
                  type="button"
                  onClick={() => onPreview(doc)}
                  className="block w-full aspect-[4/3] bg-app-surface-muted overflow-hidden"
                  aria-label={`${doc.filename} öffnen`}
                >
                  <LeadDocumentThumbnail src={docUrl} filename={doc.filename} />
                </button>
                {isLeadDocumentDeletable(doc) ? (
                  <button
                    type="button"
                    onClick={() => onDelete(doc.id)}
                    className="absolute top-1.5 right-1.5 p-1.5 rounded-md bg-app-surface/80 text-slate-400 shadow-sm backdrop-blur-sm transition-colors hover:bg-app-surface hover:text-red-600"
                    aria-label={`${doc.filename} löschen`}
                    title="Löschen"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                ) : null}
                <div className="p-2.5 sm:p-3">
                  <p className="text-sm font-medium text-app-text truncate" title={doc.filename}>
                    {doc.filename}
                  </p>
                  <p className="text-xs text-app-text-subtle mt-0.5">
                    {formatLeadDocumentSize(doc.size)} · {formatDateToDDMMYYYY(doc.created_at.slice(0, 10))}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function OtherFilesSection({
  other,
  onPreview,
  onDelete,
}: {
  other: LeadDocument[];
  onPreview: (doc: LeadDocument) => void;
  onDelete: (docId: number) => void;
}) {
  if (other.length === 0) return null;

  return (
    <section>
      <div className="flex items-center gap-2 flex-wrap mb-1">
        <FileText className="w-4 h-4 text-app-text-subtle" />
        <h3 className="text-lg font-bold text-app-text">Weitere Dateien</h3>
        <CountBadge label="Dateien" count={other.length} />
      </div>
      <p className="text-sm text-app-text-subtle mb-3">DOC, XLS, ZIP und andere Formate</p>
      <div className="sm:hidden divide-y divide-app-border border border-app-border rounded-lg overflow-hidden">
        {other.map((doc) => (
            <div key={doc.id} className="flex items-center gap-3 px-4 py-3 bg-app-surface">
              <FileTypeBadge filename={doc.filename} className="w-12 h-12" />
              <div className="min-w-0 flex-1">
                <button
                  type="button"
                  onClick={() => onPreview(doc)}
                  className="text-sm font-medium text-app-text hover:text-pipedrive-blue truncate block text-left max-w-full"
                >
                  {doc.filename}
                </button>
                <p className="text-xs text-slate-400">
                  {formatLeadDocumentSize(doc.size)} · {formatDateToDDMMYYYY(doc.created_at.slice(0, 10))}
                </p>
              </div>
              <DocumentActions
                compact
                onOpen={() => onPreview(doc)}
                onDelete={isLeadDocumentDeletable(doc) ? () => onDelete(doc.id) : undefined}
              />
            </div>
          ))}
      </div>
      <div className="hidden sm:block border border-app-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-app-surface-muted border-b border-app-border">
              <th className="px-4 py-3 text-left font-semibold text-app-text-muted">Dateiname</th>
              <th className="px-4 py-3 text-left font-semibold text-app-text-muted">Größe</th>
              <th className="px-4 py-3 text-left font-semibold text-app-text-muted">Zuletzt geändert</th>
              <th className="px-4 py-3 text-left font-semibold text-app-text-muted w-28">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {other.map((doc) => (
                <tr key={doc.id} className="border-b border-app-border hover:bg-app-hover/50">
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <FileTypeBadge filename={doc.filename} />
                      <div>
                        <button
                          type="button"
                          onClick={() => onPreview(doc)}
                          className="font-medium text-app-text hover:text-pipedrive-blue hover:underline text-left"
                        >
                          {doc.filename}
                        </button>
                        <p className="text-xs text-app-text-subtle">{formatLeadDocumentSize(doc.size)}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-app-text-muted">{formatLeadDocumentSize(doc.size)}</td>
                  <td className="px-4 py-4 text-app-text-muted">
                    {formatDateToDDMMYYYY(doc.created_at.slice(0, 10))}
                  </td>
                  <td className="px-4 py-4">
                    <DocumentActions
                      onOpen={() => onPreview(doc)}
                      onDelete={isLeadDocumentDeletable(doc) ? () => onDelete(doc.id) : undefined}
                    />
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function LeadDocumentSections({
  leadId,
  documents,
  documentSort,
  onToggleSort,
  selectedDocIds,
  onToggleSelectAll,
  onToggleSelectDoc,
  onPreview,
  onDelete,
}: {
  leadId: string | number;
  documents: LeadDocument[];
  documentSort: DocumentSort;
  onToggleSort: (key: DocumentSort['key']) => void;
  selectedDocIds: Set<number>;
  onToggleSelectAll: () => void;
  onToggleSelectDoc: (id: number) => void;
  onPreview: (doc: LeadDocument) => void;
  onDelete: (docId: number) => void;
}) {
  const { pdfs, images, other } = React.useMemo(() => partitionLeadDocuments(documents), [documents]);
  return (
    <>
      <PdfDocumentsSection
        pdfs={pdfs}
        documentSort={documentSort}
        onToggleSort={onToggleSort}
        selectedDocIds={selectedDocIds}
        onToggleSelectAll={onToggleSelectAll}
        onToggleSelectDoc={onToggleSelectDoc}
        onPreview={onPreview}
        onDelete={onDelete}
      />
      <ImagesSection leadId={leadId} images={images} onPreview={onPreview} onDelete={onDelete} />
      <OtherFilesSection other={other} onPreview={onPreview} onDelete={onDelete} />
    </>
  );
}

export function LeadDocumentsTab({
  leadId,
  documents,
  documentsLoading,
  documentsUploading,
  documentsUploadProgress,
  documentsUploadError,
  isDragging,
  documentSearch,
  onDocumentSearchChange,
  documentSort,
  onToggleSort,
  selectedDocIds,
  onToggleSelectAll,
  onToggleSelectDoc,
  filteredAndSortedDocuments,
  fileInputRef,
  cameraInputRef,
  onUploadDocument,
  onDrop,
  onDragOver,
  onDragLeave,
  onPreview,
  onDelete,
  onCreateDocument,
  embedded = false,
}: LeadDocumentsTabProps) {
  const { pdfs, images, other } = React.useMemo(
    () => partitionLeadDocuments(filteredAndSortedDocuments),
    [filteredAndSortedDocuments],
  );

  const hasAnyFiles = documents.length > 0;

  const openFilePicker = () => {
    if (documentsUploading) return;
    fileInputRef.current?.click();
  };

  return (
    <div
      className={
        embedded
          ? 'min-w-0'
          : 'app-scrollbar flex-1 min-h-0 overflow-y-auto overscroll-contain p-0 sm:p-6'
      }
    >
      <input ref={fileInputRef} type="file" className="hidden" accept="*/*" multiple onChange={onUploadDocument} />
      <input
        ref={cameraInputRef}
        type="file"
        className="hidden"
        accept="image/*"
        capture="environment"
        onChange={onUploadDocument}
      />

      {embedded ? null : (
        <h2 className="hidden sm:flex text-sm font-bold text-slate-400 uppercase tracking-widest items-center gap-2 mb-4">
          <FileText className="w-4 h-4" />
          Dokumente
        </h2>
      )}

      <div className="sm:hidden flex items-center gap-2 px-3 py-2.5 bg-app-surface border-b border-app-border sticky top-0 z-10">
        <button
          type="button"
          onClick={onCreateDocument}
          className="p-2.5 rounded-lg bg-pipedrive-blue/10 text-pipedrive-blue hover:bg-pipedrive-blue/20 shrink-0"
          aria-label="Dokument erstellen"
        >
          <FilePlus className="w-4 h-4" />
        </button>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          <input
            type="text"
            value={documentSearch}
            onChange={(e) => onDocumentSearchChange(e.target.value)}
            placeholder="Dokumente suchen…"
            className="w-full pl-9 pr-3 py-2 border border-app-border rounded-lg text-sm focus:ring-2 focus:ring-pipedrive-blue/20 focus:border-pipedrive-blue outline-none"
          />
        </div>
        <button
          type="button"
          disabled={documentsUploading}
          onClick={() => fileInputRef.current?.click()}
          className="p-2.5 rounded-lg bg-app-surface-muted text-app-text-muted hover:bg-slate-200 disabled:opacity-50 shrink-0"
          aria-label="Datei hochladen"
        >
          <Upload className="w-4 h-4" />
        </button>
        <button
          type="button"
          disabled={documentsUploading}
          onClick={() => cameraInputRef.current?.click()}
          className="p-2.5 rounded-lg bg-app-surface-muted text-app-text-muted hover:bg-slate-200 disabled:opacity-50 shrink-0"
          aria-label="Foto aufnehmen"
        >
          <Camera className="w-4 h-4" />
        </button>
      </div>

      {documentsUploading ? (
        <div className="sm:hidden px-3 py-2">
          <DocumentUploadProgressBanner progress={documentsUploadProgress} />
        </div>
      ) : null}

      <div className="bg-app-surface border-0 sm:border sm:border-app-border sm:rounded-lg p-0 sm:p-6">
        {documentsUploadError ? (
          <p className="mx-4 sm:mx-0 mb-3 text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
            {documentsUploadError}
          </p>
        ) : null}
        <div className="hidden sm:block">
          <div
            data-file-drop-zone
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onClick={openFilePicker}
            className={`relative flex min-h-[240px] flex-col items-center rounded-xl border-2 border-dashed px-6 py-5 transition-colors select-none ${
              documentsUploading ? 'cursor-default' : 'cursor-pointer'
            } ${
              isDragging
                ? 'border-sky-500 bg-sky-50/80'
                : documentsUploading
                  ? 'border-sky-400 bg-sky-50/60'
                  : 'border-sky-200 bg-app-surface-muted/50 hover:border-sky-300 hover:bg-sky-50/50'
            }`}
          >
            {documentsUploading ? <DocumentUploadProgressOverlay progress={documentsUploadProgress} /> : null}
            <div
              className={`mt-auto flex flex-col items-center gap-3 text-center w-full ${
                documentsUploading ? 'invisible' : ''
              }`}
            >
              <div className="w-14 h-14 rounded-full bg-sky-100 flex items-center justify-center text-sky-500">
                <Upload className="w-7 h-7" />
              </div>
              <p className="text-app-text-muted text-sm">
                Dateien hier ablegen oder <span className="text-sky-600 underline font-medium">Dateien wählen</span>
              </p>
            </div>
            <div className={`flex w-full flex-wrap justify-between gap-x-4 gap-y-1 mt-6 text-xs text-app-text-subtle ${documentsUploading ? 'invisible' : ''}`}>
              <span>Unterstützte Formate: PDF, DOC, XLS, Bilder, alle Formate</span>
              <span>Max. Größe: {Math.round(MAX_LEAD_DOCUMENT_UPLOAD_BYTES / (1024 * 1024))} MB</span>
            </div>
          </div>
        </div>

        {documentsLoading ? (
          <InlineLoadingState label="Lade Dokumente..." className="mt-6 justify-start px-4 sm:px-0" />
        ) : (
          <div className="sm:mt-6">
            <div className="hidden sm:flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <CountBadge label="PDFs" count={pdfs.length} />
                  <CountBadge label="Bilder" count={images.length} />
                  <CountBadge label="Sonstige" count={other.length} />
                </div>
                <p className="text-sm text-app-text-subtle mt-1">Hier können Sie die hochgeladenen Dateien durchsuchen.</p>
              </div>
              <div className="relative">
                <input
                  type="text"
                  value={documentSearch}
                  onChange={(e) => onDocumentSearchChange(e.target.value)}
                  placeholder="Suchen..."
                  className="w-48 sm:w-56 pl-3 pr-9 py-2 border border-app-border rounded-lg text-sm focus:ring-2 focus:ring-pipedrive-blue/20 focus:border-pipedrive-blue outline-none"
                />
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              </div>
            </div>

            {!hasAnyFiles ? (
              <p className="text-sm text-app-text-subtle py-8 text-center px-4">Noch keine Dokumente vorhanden.</p>
            ) : (
              <LeadDocumentSections
                leadId={leadId}
                documents={filteredAndSortedDocuments}
                documentSort={documentSort}
                onToggleSort={onToggleSort}
                selectedDocIds={selectedDocIds}
                onToggleSelectAll={onToggleSelectAll}
                onToggleSelectDoc={onToggleSelectDoc}
                onPreview={onPreview}
                onDelete={onDelete}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
